"use client";

import React from "react";
import type { BrandConfig } from "@/types/ai";

export type BackgroundMotionConfig = NonNullable<BrandConfig["backgroundMotion"]>;

const DEFAULT_MOTION: Required<NonNullable<BackgroundMotionConfig>> = {
  enabled: true,
  type: "blob",
  direction: "none",
  speed: "normal",
  randomness: 0.5,
  intensity: 0.8,
};

function getDuration(speed: "slow" | "normal" | "fast", base: number): string {
  const mult = speed === "slow" ? 1.6 : speed === "fast" ? 0.55 : 1;
  return `${Math.round(base * mult)}s`;
}

type Props = {
  primaryColor: string;
  secondaryColor: string;
  motion?: BackgroundMotionConfig | null;
  className?: string;
};

/** Renders the animated background layer for AI surfaces. Motion type, direction, speed, randomness and intensity are configurable. */
export default function AIBackgroundMotion({
  primaryColor,
  secondaryColor,
  motion,
  className = "",
}: Props) {
  const m = { ...DEFAULT_MOTION, ...motion };
  const intensity = Math.max(0.2, Math.min(1, m.intensity ?? 0.8));
  const opacity = 0.08 + intensity * 0.2;
  const durationBlob = getDuration(m.speed ?? "normal", 7);
  const durationRipple = getDuration(m.speed ?? "normal", 4);
  const durationWaves = getDuration(m.speed ?? "normal", 12);
  const durationPulse = getDuration(m.speed ?? "normal", 3);
  const durationShift = getDuration(m.speed ?? "normal", 8);
  const durationGradient = getDuration(m.speed ?? "normal", 15);

  const dir = m.direction ?? "none";
  const driftX = dir === "left" ? -8 : dir === "right" ? 8 : 0;
  const driftY = dir === "up" ? -8 : dir === "down" ? 8 : 0;
  const random = Math.max(0, Math.min(1, m.randomness ?? 0.5));
  const vary = (base: number) => base + (random * base * 0.5);

  if (m.enabled === false || m.type === "none") {
    return (
      <div className={`absolute inset-0 overflow-hidden pointer-events-none z-0 ${className}`}>
        <div
          className="absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl"
          style={{ backgroundColor: primaryColor, opacity: 0.12 }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl"
          style={{ backgroundColor: secondaryColor, opacity: 0.12 }}
        />
      </div>
    );
  }

  const blobStyle = {
    opacity,
    animationDuration: durationBlob,
  };

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none z-0 ${className}`}>
      {m.type === "gradient" && (
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 25%, ${primaryColor} 50%, ${secondaryColor} 75%, ${primaryColor} 100%)`,
            backgroundSize: "400% 400%",
            animation: "gradientShift ease infinite",
            animationDuration: durationGradient,
            opacity: 0.2 * intensity,
          }}
        />
      )}

      {m.type === "blob" && (
        <>
          <div
            className="absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl animate-blob"
            style={{ ...blobStyle, backgroundColor: primaryColor }}
          />
          <div
            className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"
            style={{ ...blobStyle, backgroundColor: secondaryColor, animationDelay: "2s" }}
          />
        </>
      )}

      {m.type === "ripple" && (
        <>
          <div
            className="absolute top-1/2 left-1/2 w-[80vmax] h-[80vmax] -translate-x-1/2 -translate-y-1/2 rounded-full mix-blend-multiply filter blur-3xl animate-bg-ripple"
            style={{
              backgroundColor: primaryColor,
              opacity: intensity * 0.2,
              animationDuration: durationRipple,
            }}
          />
          <div
            className="absolute top-1/2 left-1/2 w-[70vmax] h-[70vmax] -translate-x-1/2 -translate-y-1/2 rounded-full mix-blend-multiply filter blur-3xl animate-bg-ripple-2"
            style={{
              backgroundColor: secondaryColor,
              opacity: intensity * 0.15,
              animationDuration: durationRipple,
              animationDelay: "1s",
            }}
          />
        </>
      )}

      {m.type === "waves" && (
        <>
          <div
            className="absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl animate-bg-waves"
            style={{
              backgroundColor: primaryColor,
              opacity,
              animationDuration: durationWaves,
              transform: `translate(${driftX}px, ${driftY}px)`,
            }}
          />
          <div
            className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl animate-bg-waves animation-delay-2000"
            style={{
              backgroundColor: secondaryColor,
              opacity,
              animationDuration: durationWaves,
              animationDelay: "2s",
              transform: `translate(${-driftX}px, ${-driftY}px)`,
            }}
          />
        </>
      )}

      {m.type === "pulse" && (
        <>
          <div
            className="absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl animate-bg-pulse"
            style={{
              backgroundColor: primaryColor,
              animationDuration: durationPulse,
            }}
          />
          <div
            className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl animate-bg-pulse animation-delay-2000"
            style={{
              backgroundColor: secondaryColor,
              animationDuration: durationPulse,
              animationDelay: "1.2s",
            }}
          />
        </>
      )}

      {m.type === "shift" && (
        <>
          <div
            className={`absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl ${random > 0.5 ? "animate-bg-shift" : "animate-bg-shift-subtle"}`}
            style={{
              backgroundColor: primaryColor,
              opacity,
              animationDuration: durationShift,
              transform: `translate(${vary(driftX)}px, ${vary(driftY)}px)`,
            }}
          />
          <div
            className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl animation-delay-2000 ${random > 0.5 ? "animate-bg-shift" : "animate-bg-shift-subtle"}`}
            style={{
              backgroundColor: secondaryColor,
              opacity,
              animationDuration: durationShift,
              animationDelay: "2s",
              transform: `translate(${vary(-driftX)}px, ${vary(-driftY)}px)`,
            }}
          />
        </>
      )}
    </div>
  );
}
